import { useState, useEffect } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/free-solid-svg-icons';

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona (MST)' },
  { value: 'America/Anchorage', label: 'Alaska (AKST)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
  { value: 'UTC', label: 'UTC' },
];

export default function ProfileSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const [language, setLanguage] = useState('en');
  const [profileImageError, setProfileImageError] = useState(false);

  // Reset profile image error when user or photoURL changes
  useEffect(() => {
    setProfileImageError(false);
  }, [user?.photoURL]);

  useEffect(() => {
    if (user) {
      // Load user preferences
      const loadPreferences = async () => {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setDisplayName(data.displayName || user.displayName || '');
            setTimezone(data.preferences?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York');
            setLanguage(data.preferences?.language || 'en');
          } else {
            // Set defaults from Firebase Auth
            setDisplayName(user.displayName || '');
            setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York');
          }
        } catch (error) {
          console.error('Error loading preferences:', error);
        }
      };
      loadPreferences();
    }
  }, [user]);

  const handleSave = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      const currentData = userDoc.exists() ? userDoc.data() : {};

      await updateDoc(userDocRef, {
        displayName: displayName || user.displayName,
        preferences: {
          ...currentData.preferences,
          timezone,
          language,
        },
        updatedAt: new Date(),
      });

      toast.success('Profile settings updated!');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-card shadow-card border border-border p-6 sm:p-8">
      <h2 className="text-xl sm:text-2xl font-semibold text-text-primary mb-6">Profile Settings</h2>

      {/* Profile Photo */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-text-primary mb-3">Profile Photo</label>
        <div className="flex items-center gap-4">
          {user?.photoURL && !profileImageError ? (
            <img 
              src={user.photoURL} 
              alt={user.displayName || 'Profile'} 
              className="w-20 h-20 rounded-full border-2 border-border"
              onError={() => {
                console.error('Profile picture failed to load:', user.photoURL);
                setProfileImageError(true);
              }}
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center border-2 border-border">
              <span className="text-primary text-3xl font-semibold">
                {user?.displayName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || '👤'}
              </span>
            </div>
          )}
          <div>
            <p className="text-sm text-text-secondary mb-1">
              Photo from Google account
            </p>
            <p className="text-xs text-text-secondary">
              Custom photo upload coming soon
            </p>
          </div>
        </div>
      </div>

      {/* Display Name */}
      <div className="mb-6">
        <label htmlFor="displayName" className="block text-sm font-medium text-text-primary mb-2">
          Display Name
        </label>
        <input
          type="text"
          id="displayName"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className="w-full px-4 py-2.5 text-base border border-border rounded-lg focus:outline-none focus:border-primary transition-all"
          placeholder="Your name"
        />
      </div>

      {/* Email (Read-only) */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-text-primary mb-2">
          Email
        </label>
        <input
          type="email"
          value={user?.email || ''}
          disabled
          className="w-full px-4 py-2.5 text-base border border-border rounded-lg bg-surface text-text-secondary cursor-not-allowed"
        />
        <p className="text-xs text-text-secondary mt-1">
          Email is managed through your Google account
        </p>
      </div>

      {/* Timezone */}
      <div className="mb-6">
        <label htmlFor="timezone" className="block text-sm font-medium text-text-primary mb-2">
          Timezone
        </label>
        <select
          id="timezone"
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          className="w-full px-4 py-2.5 text-base border border-border rounded-lg focus:outline-none focus:border-primary transition-all bg-white"
        >
          {TIMEZONES.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {tz.label}
            </option>
          ))}
        </select>
      </div>

      {/* Language (Stub) */}
      <div className="mb-6">
        <label htmlFor="language" className="block text-sm font-medium text-text-primary mb-2">
          Language
        </label>
        <select
          id="language"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          disabled
          className="w-full px-4 py-2.5 text-base border border-border rounded-lg bg-surface text-text-secondary cursor-not-allowed"
        >
          <option value="en">English</option>
        </select>
        <p className="text-xs text-text-secondary mt-1">
          Additional languages coming soon
        </p>
      </div>

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={loading}
        className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-all shadow-button hover:shadow-button-hover flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FontAwesomeIcon icon={faSave} />
        <span>{loading ? 'Saving...' : 'Save Changes'}</span>
      </button>
    </div>
  );
}

